import type { OnBeforeRouteSync } from "vike/types";
import { delocalizeRoute } from '../utils/constants/url-constants';
import { inferLocaleFromLocalizedPathRoot } from '../utils/routing/infer-locale-from-localized-path-root';
import {
  isRemovedSalaryComparisonPathnameWithoutLocale,
  REMOVED_SALARY_COMPARISON_URL_LOGICAL,
} from '../utils/routing/removed-salary-comparison-path';

export const onBeforeRoute: OnBeforeRouteSync = (pageContext) => {
  const { urlOriginal, urlParsed } = pageContext;

  if (!urlParsed) {
    return {
      pageContext: {
        locale: "en",
        urlLogical: "/",
      },
    };
  }

  const { locale, pathnameWithoutLocale } = extractLocale(urlParsed.pathname);

  // Retired salary comparison URLs (410) — map all locales to one Vike route
  if (isRemovedSalaryComparisonPathnameWithoutLocale(pathnameWithoutLocale)) {
    return {
      pageContext: {
        locale,
        urlLogical: REMOVED_SALARY_COMPARISON_URL_LOGICAL,
      },
    };
  }
  
  // Check if this is a resource/blog page (tyoelama, arbetsliv, resources)
  // Skip delocalization for these to allow language-specific content
  const isResourcePath = pathnameWithoutLocale.startsWith('/tyoelama') || 
                         pathnameWithoutLocale.startsWith('/arbetsliv') || 
                         pathnameWithoutLocale.startsWith('/resources');

  const isJobSearchGuidePath =
    pathnameWithoutLocale.startsWith('/job-search') ||
    pathnameWithoutLocale.startsWith('/tyonhaku') ||
    pathnameWithoutLocale.startsWith('/jobbsok');
  
  let urlLogical;
  
  if (isResourcePath || isJobSearchGuidePath) {
    // Don't delocalize resource paths - keep them as-is for language-specific content
    urlLogical = pathnameWithoutLocale;
  } else {
    // Convert localized path back to English equivalent for routing
    urlLogical = delocalizeRoute(pathnameWithoutLocale, locale as 'en' | 'fi' | 'sv');
  }

  return {
    pageContext: {
      locale,
      urlLogical,
    },
  };
};

function extractLocale(pathname: string): { locale: string; pathnameWithoutLocale: string } {
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return { locale: "en", pathnameWithoutLocale: "/" };
  }

  const firstSegment = segments[0].toLowerCase();

  // Check if the first segment is a locale
  if (["en", "fi", "sv"].includes(firstSegment)) {
    const pathnameWithoutLocale = "/" + segments.slice(1).join("/");
    return {
      locale: firstSegment,
      pathnameWithoutLocale: pathnameWithoutLocale || "/",
    };
  }

  // URLs without /en|fi|sv/ prefix (e.g. /tyoelama/..., /tyopaikat/..., /resources/...)
  const inferred = inferLocaleFromLocalizedPathRoot(firstSegment);
  if (inferred) {
    return {
      locale: inferred,
      pathnameWithoutLocale: pathname,
    };
  }

  // Default to English if no locale is specified
  return {
    locale: "en",
    pathnameWithoutLocale: pathname,
  };
} 